import { differenceInMinutes, format, startOfDay } from "date-fns"

import {
  type HourMarker,
  ROW_HEIGHT,
  TIMELINE_START_HOUR,
  TOTAL_HOURS,
  timeToPosition,
} from "@/lib/timelineScale"

export function HourHeader({
  hourMarkers,
  width,
}: {
  hourMarkers: HourMarker[]
  /** The tracks' width plus the room their vertical scrollbar takes. */
  width: number
}) {
  return (
    <div className="relative h-full" style={{ width: `${width}px` }}>
      {hourMarkers.map((marker) => (
        <div
          key={marker.hour}
          className="absolute top-0 flex h-full flex-col items-center justify-center border-l border-palette-jungleGreen200"
          style={{ left: `${marker.position}px` }}
        >
          <span className="px-2 text-xs font-medium text-palette-jungleGreen500">
            {marker.label}
          </span>
        </div>
      ))}
    </div>
  )
}

export function GridLines({
  hourMarkers,
  hourWidth,
  rowCount,
  selectedDate,
  currentTime,
}: {
  hourMarkers: HourMarker[]
  hourWidth: number
  rowCount: number
  selectedDate: Date
  currentTime: Date
}) {
  const height = `${rowCount * ROW_HEIGHT}px`
  return (
    <>
      <div
        className="absolute z-10 w-0.5 bg-amber-400"
        style={{
          left: `${24 * hourWidth}px`,
          top: 0,
          height,
        }}
      />

      {hourMarkers.map((marker) => (
        <div
          key={`grid-${marker.hour}`}
          className="absolute w-px bg-palette-jungleGreen100"
          style={{
            left: `${marker.position}px`,
            top: 0,
            height,
          }}
        />
      ))}

      <CurrentTimeLine
        selectedDate={selectedDate}
        currentTime={currentTime}
        left={timeToPosition(currentTime, selectedDate, hourWidth)}
        height={height}
      />
    </>
  )
}

function CurrentTimeLine({
  selectedDate,
  currentTime,
  left,
  height,
}: {
  selectedDate: Date
  currentTime: Date
  left: number
  height: string
}) {
  const startOfTimeline = startOfDay(selectedDate)
  startOfTimeline.setHours(TIMELINE_START_HOUR)
  const minutesSinceStart = differenceInMinutes(currentTime, startOfTimeline)
  const hoursSinceStart = minutesSinceStart / 60
  const isWithinTimeline = hoursSinceStart >= 0 && hoursSinceStart <= TOTAL_HOURS
  if (!isWithinTimeline) return null

  return (
    <div
      className="absolute z-30 w-0.5 bg-rose-500"
      style={{
        left: `${left}px`,
        top: 0,
        height,
      }}
    >
      <div className="sticky top-0">
        <div className="absolute -left-1.5 -top-1 h-3 w-3 rounded-full bg-rose-500" />
        <div className="absolute -left-6 -top-5 whitespace-nowrap rounded bg-rose-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {format(currentTime, "HH:mm")}
        </div>
      </div>
    </div>
  )
}
